import { useMemo, useRef } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Clear from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "@/shared/components";
import { useSignIn } from "@/partner/entities/auth";
import { Field, Form } from "react-final-form";
import { makeSignInSchema, type SignInFormData } from "../model";
import { validateWithZod } from "@/shared/utils/zod";

interface SignInFormProps {
  onCompleted: (data: any, input?: { email?: string }) => void;
}

export function SignInForm({ onCompleted }: SignInFormProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [signIn, { loading, error }] = useSignIn();

  const validate = useMemo(() => validateWithZod(makeSignInSchema(t)), [t]);

  const onSubmit = (input: SignInFormData) => {
    signIn({
      variables: { input },
      onCompleted: (data) => onCompleted(data, input),
    });
  };

  return (
    <Form
      initialValues={{
        email: "",
      }}
      onSubmit={onSubmit}
      validate={validate}
      render={({ valid, handleSubmit }) => (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {error && <ErrorMessage error={error} />}

            <Field
              name="email"
              parse={(v) => v ?? ""}
              render={({ input }) => (
                <TextField
                  {...input}
                  type="email"
                  label={t("auth.signIn.emailLabel")}
                  placeholder="your@email.com"
                  autoFocus
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  slotProps={{
                    input: {
                      ref: inputRef,
                      inputMode: "text",
                      endAdornment: input.value && (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t("common.a11y.clearInput")}
                            onClick={() => input.onChange("")}
                            edge="end"
                            size="small"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || !valid}
            >
              {t("auth.signIn.submit")}
            </Button>
          </Stack>
        </Box>
      )}
    />
  );
}
